'use client';
import { useEffect, useRef, useState } from 'react';

import { SkeletonLoader } from '../Loading/SkeletonLoader';
import { generateSrcSet } from './GenerateSrcSet';
import { heycarLoader } from './HeycarLoader';

import styles from './HeycarImage.module.css';

type Props = {
  src: string;
  alt: string;
  sizes: string;
  className?: string;
  forceVisible?: boolean;
  loader?: ({ src, width }: { src: string; width: number }) => string;
  preload?: boolean;
  isInactive?: boolean;
  srcSetWidths?: number[];
  isStandAlone?: boolean;
};

export const HeycarImage = ({
  src,
  alt,
  sizes,
  className = '',
  forceVisible = false,
  loader = heycarLoader,
  preload = false,
  isInactive = false,
  srcSetWidths,
  isStandAlone,
  ...rest
}: Props) => {
  const [isInViewport, setIsInViewport] = useState<boolean | undefined>(forceVisible);
  const [hasLoaded, setHasLoaded] = useState<boolean>(true);
  const $imageRef = useRef<HTMLImageElement>(null);
  const $containerRef = useRef<HTMLDivElement>(null);
  const isComplete = $imageRef?.current?.complete;
  const height = $imageRef?.current?.naturalHeight;

  useEffect(() => {
    const hasHeight = height !== 0;

    const isSafari = navigator.userAgent.toLowerCase().indexOf('safari/') > -1;

    if (isComplete && hasHeight) {
      setHasLoaded(true);
    } else if (!isSafari) {
      setHasLoaded(false);
    }
  }, [isComplete, height]);

  useEffect(() => {
    if (!$containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInViewport(true);
      },
      { root: null, rootMargin: '0px', threshold: 0 },
    );

    observer.observe($containerRef.current);

    return () => {
      if (!$containerRef.current) return;
      observer.unobserve($containerRef.current);
    };
  }, [$containerRef.current]);

  return (
    <>
      {!hasLoaded && isInViewport && <SkeletonLoader width={'100%'} height={'100%'} />}
      <div
        ref={$containerRef}
        className={`${styles.container} ${isStandAlone ? styles.isStandAlone : ''}`}
        data-has-loaded={hasLoaded}
        data-is-visible={isInViewport}
        key={src}
        {...rest}
      >
        {isInViewport && (
          <img
            className={`${className} ${styles.image}`}
            ref={$imageRef}
            src={src}
            srcSet={generateSrcSet(src, loader, srcSetWidths)}
            sizes={sizes}
            alt={alt}
            onLoad={() => setHasLoaded(true)}
            fetchPriority={preload ? 'high' : undefined}
            loading={preload ? 'eager' : 'lazy'}
            data-is-inactive={isInactive}
          />
        )}
      </div>
    </>
  );
};
